import { manifestEntries } from '../domains';
import type { DomainParents, DomainRouteManifest } from './domain-routes';
import { Route as root } from './pages/__root';
import { createAuthenticatedRoute } from './pages/_authenticated';
import { Route as bare } from './pages/_bare';
import { createPublicRoute } from './pages/_public';
import { collectQuickActions, registerQuickActions } from './quickActions';
import createRouterRoutes from './routes';

// Router-owned routes (root index, error, forbidden) live outside `domains/` and
// are wired explicitly. Appended last so they keep first-match-wins semantics
// consistent with translatedPathnames.ts.
const factories = [...manifestEntries.map(([, factory]) => factory), createRouterRoutes];

function lateBoundParentMessage(name: 'authenticated' | 'public'): string {
  return (
    `parents.${name} must only be referenced inside getParentRoute closures; ` +
    'it is bound after every domain manifest has contributed its guards.'
  );
}

// Both layout parents are built only after we've collected the guards every domain wants to
// contribute. Domain factories may only reference them inside `getParentRoute` closures, which
// TanStack evaluates lazily during route-tree composition. The getters below enforce that contract:
// any synchronous read inside a factory throws with a clear message instead of silently leaking
// `undefined` into TanStack internals.
let authenticated: ReturnType<typeof createAuthenticatedRoute> | null = null;
let publicRoute: ReturnType<typeof createPublicRoute> | null = null;
const parents: DomainParents = {
  bare,
  get public() {
    if (!publicRoute) {
      throw new Error(lateBoundParentMessage('public'));
    }
    return publicRoute;
  },
  get authenticated() {
    if (!authenticated) {
      throw new Error(lateBoundParentMessage('authenticated'));
    }
    return authenticated;
  },
};

const domainRouteManifests: DomainRouteManifest[] = factories.map((create) => create(parents));

authenticated = createAuthenticatedRoute(domainRouteManifests.flatMap((m) => m.authenticatedGuards ?? []));
publicRoute = createPublicRoute(domainRouteManifests.flatMap((m) => m.publicGuards ?? []));

const publicDomainRoutes = domainRouteManifests.flatMap((m) => m.public ?? []);
const authenticatedDomainRoutes = domainRouteManifests.flatMap((m) => m.authenticated ?? []);
const bareDomainRoutes = domainRouteManifests.flatMap((m) => m.bare ?? []);

registerQuickActions(collectQuickActions([...authenticatedDomainRoutes, ...publicDomainRoutes, ...bareDomainRoutes]));

export const routeTree = root.addChildren([
  publicRoute.addChildren(publicDomainRoutes),
  authenticated.addChildren(authenticatedDomainRoutes),
  bare.addChildren(bareDomainRoutes),
]);
